/**
 * Skills, the experience curve, and combat level.
 *
 * Skill names are kept familiar because they are generic. Items, NPCs, and places
 * are named originally. See docs/PLAN.md section 12.
 */

import { MAX_EXPERIENCE, MAX_LEVEL } from './constants';

/**
 * Every skill the game keeps a level in.
 *
 * Twenty, not the usual twenty three. Three of them were dropped because they
 * were not skills so much as habits: Fletching is Crafting done to a bow, and
 * lives there now. Runecrafting is Magic done sitting down, and is folded into
 * Magic, which is trained by making what it casts rather than by throwing
 * spells at a wall. Slayer is not a skill at all, it is a list of things
 * somebody was told to kill, and it comes back as a reputation with a guild
 * that hands the list out.
 *
 * Two are renamed and work exactly as they did. Herblore is Alchemy, which is
 * what it always was, and Prayer is Faith, which asks less of a player about
 * what they believe.
 */
export enum Skill {
  Attack = 0,
  Defence = 1,
  Strength = 2,
  Constitution = 3,
  Ranged = 4,
  Faith = 5,
  Magic = 6,
  Cooking = 7,
  Woodcutting = 8,
  Fishing = 9,
  Firemaking = 10,
  Crafting = 11,
  Smithing = 12,
  Mining = 13,
  Alchemy = 14,
  Agility = 15,
  Thieving = 16,
  Farming = 17,
  Hunter = 18,
  Construction = 19,
}

export const SKILL_COUNT = 20;

export const SKILL_NAMES: readonly string[] = [
  'Attack',
  'Defence',
  'Strength',
  'Constitution',
  'Ranged',
  'Faith',
  'Magic',
  'Cooking',
  'Woodcutting',
  'Fishing',
  'Firemaking',
  'Crafting',
  'Smithing',
  'Mining',
  'Alchemy',
  'Agility',
  'Thieving',
  'Farming',
  'Hunter',
  'Construction',
];

/**
 * The same names in the form content is written in.
 *
 * One list rather than one per panel: anything that names a skill in a data
 * file, a condition or a reward is checked against this, so a skill the game
 * keeps a level in is never a skill the tools cannot say. Written out rather
 * than derived so each name is a type of its own, and checked against
 * SKILL_NAMES by a test.
 */
export const SKILL_KEYS = [
  'attack',
  'defence',
  'strength',
  'constitution',
  'ranged',
  'faith',
  'magic',
  'cooking',
  'woodcutting',
  'fishing',
  'firemaking',
  'crafting',
  'smithing',
  'mining',
  'alchemy',
  'agility',
  'thieving',
  'farming',
  'hunter',
  'construction',
] as const;

export type SkillKey = (typeof SKILL_KEYS)[number];

/** Every skill, in wire order. Iterating this keeps indexes and the enum in step. */
export const ALL_SKILLS = Array.from({ length: SKILL_COUNT }, (_, index) => index) as Skill[];

/** Skills that count toward combat level. */
export const COMBAT_SKILLS: readonly Skill[] = [
  Skill.Attack,
  Skill.Defence,
  Skill.Strength,
  Skill.Constitution,
  Skill.Ranged,
  Skill.Faith,
  Skill.Magic,
];

/** Skills that start at level 10 rather than level 1. */
export const STARTING_LEVELS: Readonly<Partial<Record<Skill, number>>> = {
  [Skill.Constitution]: 10,
};

/**
 * Cumulative experience required for each level, index 0 unused so that
 * XP_TABLE[level] reads naturally. Built from the standard curve:
 * points(n) = floor(n + 300 * 2^(n/7)), xp(L) = floor(sum(points(1..L-1)) / 4)
 */
export const XP_TABLE: readonly number[] = buildXpTable();

function buildXpTable(): number[] {
  const table: number[] = [0, 0];
  let points = 0;
  for (let level = 1; level < MAX_LEVEL; level++) {
    points += Math.floor(level + 300 * Math.pow(2, level / 7));
    table[level + 1] = Math.floor(points / 4);
  }
  return table;
}

/** Experience needed to reach a level. Levels outside 1..99 clamp. */
export function experienceForLevel(level: number): number {
  if (level <= 1) return 0;
  if (level >= MAX_LEVEL) return XP_TABLE[MAX_LEVEL] ?? 0;
  return XP_TABLE[level] ?? 0;
}

/** Level reached by an amount of experience. */
export function levelForExperience(experience: number): number {
  if (experience <= 0) return 1;
  for (let level = MAX_LEVEL; level >= 2; level--) {
    if (experience >= (XP_TABLE[level] ?? Infinity)) return level;
  }
  return 1;
}

/** Experience remaining until the next level, or 0 at maximum level. */
export function experienceToNextLevel(experience: number): number {
  const level = levelForExperience(experience);
  if (level >= MAX_LEVEL) return 0;
  return experienceForLevel(level + 1) - experience;
}

/** Fraction of the way through the current level, 0 to 1. For progress bars. */
export function levelProgress(experience: number): number {
  const level = levelForExperience(experience);
  if (level >= MAX_LEVEL) return 1;
  const floor = experienceForLevel(level);
  const ceiling = experienceForLevel(level + 1);
  return (experience - floor) / (ceiling - floor);
}

export function clampExperience(experience: number): number {
  return Math.min(Math.max(0, Math.floor(experience)), MAX_EXPERIENCE);
}

export interface CombatLevels {
  attack: number;
  strength: number;
  defence: number;
  constitution: number;
  ranged: number;
  magic: number;
  faith: number;
}

/**
 * Combat level. The base is defensive, and whichever of melee, ranged, or magic
 * is highest is added on top.
 */
export function combatLevel(levels: CombatLevels): number {
  const base = 0.25 * (levels.defence + levels.constitution + Math.floor(levels.faith / 2));
  const melee = 0.325 * (levels.attack + levels.strength);
  const ranged = 0.325 * Math.floor((3 * levels.ranged) / 2);
  const magic = 0.325 * Math.floor((3 * levels.magic) / 2);
  return Math.floor(base + Math.max(melee, ranged, magic));
}

/**
 * How much mana somebody holds, from their Magic level.
 *
 * Ten a level and ten to start with, so a beginner has twenty and somebody who
 * has finished the skill has a thousand and ten. The same shape health uses,
 * because two pools that grow differently are two pools a player has to learn
 * separately for no gain.
 */
export function maxManaFor(magicLevel: number): number {
  return 10 + Math.max(1, magicLevel) * 10;
}

/**
 * How fast it comes back, in mana per tick.
 *
 * Magic decides how much there is and how quickly it returns; Constitution
 * carries some of the second, because the thing being recovered from is effort.
 * Written as a share of the pool rather than a flat number so that the wait to
 * refill stays roughly the same from one end of the skill to the other: a flat
 * rate that suits a beginner takes a quarter of an hour at the top, and one that
 * suits the top fills a beginner's pool faster than they can spend it.
 *
 * At level one that is about a fiftieth of the pool a tick, which is thirty
 * seconds to fill from empty. At ninety nine with Constitution to match it is
 * about a twentieth, which is twelve.
 */
export function manaPerTick(magicLevel: number, constitutionLevel: number): number {
  const pool = maxManaFor(magicLevel);
  const share = 0.02 + Math.max(1, magicLevel) * 0.00025 + Math.max(1, constitutionLevel) * 0.0001;

  return pool * share;
}

/**
 * The rungs of the elemental ladder, and the level each is learned at.
 *
 * All four elements of a rung are learned at once. That is the difference
 * between this and the game everybody will compare it to: there, the elements
 * are staggered, and the answer to "which spell should I be casting" is always
 * whichever one the last level unlocked, which is not a choice. Here the choice
 * is the element, made in the fighting panel against what is being fought, and
 * the tier is the thing that moves as somebody trains.
 */
export const SPELL_TIERS: readonly { name: string; level: number }[] = [
  { name: 'Strike', level: 1 },
  { name: 'Bolt', level: 10 },
  { name: 'Blast', level: 20 },
  { name: 'Wave', level: 30 },
  { name: 'Surge', level: 40 },
  { name: 'Rush', level: 50 },
  { name: 'Burst', level: 60 },
  { name: 'Blitz', level: 70 },
  { name: 'Barrage', level: 80 },
];

/** The four elements, in the order they are shown everywhere. */
export const SPELL_ELEMENTS: readonly string[] = ['air', 'water', 'earth', 'fire'];
